import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, Switch, InputNumber, Button, Card, Transfer, message } from "antd";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";
import { PagesHeader } from "../../components/Categories";

const DomainBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [allSkills, setAllSkills] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]); // Selected skill IDs

  useEffect(() => {
    fetchDomainData();
    fetchAllSkills();
  }, [id]);

  const fetchDomainData = async () => {
    setLoading(true);
    try {
      // We can fetch the domain detail from the list API for now, or just use a specific one if we had it
      // Let's get the list and find it
      const res = await axiosHttp.get("/api/v3/admin/domains");
      if (res.data?.success) {
        const found = res.data.data.find(d => d._id === id);
        if (found) {
          setDomain(found);
          form.setFieldsValue({
            name: found.name,
            slug: found.slug,
            is_promoted: found.is_promoted,
            domain_priority: found.domain_priority,
            domain_icon: found.domain_icon,
            domain_description: found.domain_description
          });
        }
      }
      
      // Fetch skills mapped to this domain
      const skillsRes = await axiosHttp.get(`/api/v3/admin/domains/${id}/skills`);
      if (skillsRes.data?.success) {
        setTargetKeys(skillsRes.data.data.map(s => s._id));
      }
    } catch (error) {
      message.error("Failed to load domain data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSkills = async () => {
    try {
      const res = await axiosHttp.get("/api/v1/skills/list?limit=1000"); 
      // Use existing v1 skill list API, or whatever admin uses for skills. 
      // Wait, admin uses /api/v3/admin/topics or /api/v1/skills/list
      // Actually v3/admin/topics returns topics. Let's use the standard skill list
      if (res.data) {
        // Adjust depending on how your skill API returns data
        const list = res.data.data || res.data; 
        setAllSkills(Array.isArray(list) ? list.map(s => ({
          key: s._id,
          title: s.name,
          description: s.code
        })) : []);
      }
    } catch (error) {
      // fallback to topics if /api/v1/skills/list fails or doesn't exist
      try {
        const fallbackRes = await axiosHttp.get("/api/v3/admin/topics");
        if (fallbackRes.data?.data) {
          setAllSkills(fallbackRes.data.data.map(s => ({
            key: s._id,
            title: s.name,
            description: s.code
          })));
        }
      } catch (err) {}
    }
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      // 1. Update Domain Settings
      await axiosHttp.put(`/api/v3/admin/domains/${id}`, values);
      
      // 2. Update Mapped Skills
      await axiosHttp.post(`/api/v3/admin/domains/${id}/skills`, {
        skillIds: targetKeys
      });
      
      message.success("Domain updated successfully!");
      navigate("/category/list");
    } catch (error) {
      message.error("Failed to save domain settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (newTargetKeys) => {
    setTargetKeys(newTargetKeys);
  };

  return (
    <Page title="Domain Builder">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PagesHeader
            pageTitle={`Domain Builder: ${domain?.name || ""}`}
            homePageUrl="/"
          />

          <div className="d-flex justify-content-end mb-4">
            <Button onClick={() => navigate("/category/list")}>Back to List</Button>
          </div>

          <div className="row">
        <div className="col-md-6">
          <Card title="Domain Settings" loading={loading}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item name="name" label="Domain Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="slug" label="URL Slug" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              
              <Form.Item name="is_promoted" label="Show in Explore Menu" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item name="domain_priority" label="Priority (Ranking in Explore menu, higher is first)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="domain_icon" label="Domain Icon URL (Optional)">
                <Input placeholder="https://..." />
              </Form.Item>
              
              <Form.Item name="domain_description" label="Short Description (Optional)">
                <Input.TextArea rows={3} />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={saving} block>
                Save Domain & Mappings
              </Button>
            </Form>
          </Card>
        </div>

        <div className="col-md-6">
          <Card title="Map Topics to this Domain" loading={loading}>
            <p>Select which topics belong to this domain. These topics will appear under this domain in the Explore menu.</p>
            <Transfer
              dataSource={allSkills}
              showSearch
              listStyle={{ width: '45%', height: 400 }}
              targetKeys={targetKeys}
              onChange={handleChange}
              render={item => item.title}
            />
          </Card>
        </div>
      </div>
        </div>
      </div>
    </Page>
  );
};

export default DomainBuilder;
